import { IHttpClient } from "@ezhou/common";

import { MappingDimension, MappingDimensionValue } from "../model/model";

export type MappingDimensionWithValues = MappingDimension & {
  values: MappingDimensionValue[];
};

/**
 * Fetches all mapping dimensions for the given organization
 */
export async function getMappingDimensions(
  httpClient: IHttpClient,
  organizationId: number
): Promise<MappingDimension[]> {
  return await httpClient.post<MappingDimension[], any>(
    "/mapping-dimensions/get-all",
    { organizationId },
    (err: any) => {
      throw err;
    }
  );
}

/**
 * Fetches all values for a specific mapping dimension
 */
export async function getMappingDimensionValues(
  httpClient: IHttpClient,
  organizationId: number,
  mappingDimensionName: string
): Promise<MappingDimensionValue[]> {
  return await httpClient.post<MappingDimensionValue[], any>(
    "/mapping-dimension-values/get-all-by-parent-id",
    {
      parentId: {
        organizationId,
        mappingDimensionName,
      },
    },
    (err: any) => {
      throw err;
    }
  );
}

/**
 * Builds a map from mapping dimension name to its values.
 * First fetches all mapping dimensions, then fetches values for each dimension.
 * 
 * @param httpClient - The HTTP client to use for API calls
 * @param organizationId - The organization ID
 * @returns A Map where keys are mapping dimension names and values are arrays of MappingDimensionValue
 */
export async function buildMappingDimensionValuesMap(
  httpClient: IHttpClient,
  organizationId: number
): Promise<Map<string, MappingDimensionValue[]>> {
  // Step 1: Get all mapping dimensions
  const mappingDimensions = await getMappingDimensions(httpClient, organizationId);

  // Step 2: For each mapping dimension, fetch its values in parallel
  const valuesPromises = mappingDimensions.map(async (dimension) => {
    const values = await getMappingDimensionValues(
      httpClient,
      organizationId,
      dimension.mappingDimensionName
    );
    return {
      dimensionName: dimension.mappingDimensionName,
      values,
    };
  });

  const results = await Promise.all(valuesPromises);

  // Step 3: Build the map
  const dimensionValuesMap = new Map<string, MappingDimensionValue[]>();
  for (const result of results) {
    dimensionValuesMap.set(result.dimensionName, result.values);
  }

  return dimensionValuesMap;
}

/**
 * Builds a map from mapping dimension name to its values (as strings).
 * This is a convenience function that extracts just the value strings.
 * 
 * @param httpClient - The HTTP client to use for API calls
 * @param organizationId - The organization ID
 * @returns A Map where keys are mapping dimension names and values are arrays of value strings
 */
export async function buildMappingDimensionValueStringsMap(
  httpClient: IHttpClient,
  organizationId: number
): Promise<Map<string, string[]>> {
  const dimensionValuesMap = await buildMappingDimensionValuesMap(httpClient, organizationId);
  
  const stringValuesMap = new Map<string, string[]>();
  for (const [dimensionName, values] of dimensionValuesMap) {
    stringValuesMap.set(dimensionName, values.map((v) => v.value));
  }
  
  return stringValuesMap;
}

/**
 * Fetches all mapping dimensions with their values embedded.
 * 
 * @param httpClient - The HTTP client to use for API calls
 * @param organizationId - The organization ID
 * @returns Array of MappingDimensionWithValues
 */
export async function getMappingDimensionsWithValues(
  httpClient: IHttpClient,
  organizationId: number
): Promise<MappingDimensionWithValues[]> {
  // Step 1: Get all mapping dimensions
  const mappingDimensions = await getMappingDimensions(httpClient, organizationId);

  // Step 2: For each mapping dimension, fetch its values in parallel
  const dimensionsWithValuesPromises = mappingDimensions.map(async (dimension) => {
    const values = await getMappingDimensionValues(
      httpClient,
      organizationId,
      dimension.mappingDimensionName
    );
    return {
      ...dimension,
      values,
    };
  });

  return await Promise.all(dimensionsWithValuesPromises);
}
